import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import { AppError } from '@/shared/lib/appError'
import { embeddingEngine } from './embeddingEngine'

export interface VectorEntry {
  id: string
  content: string
  embedding: number[]
  role: 'user' | 'assistant'
  timestamp: number
  sessionId: string
  /** Epoch ms quando a linha foi confirmada no Supabase (`local_ai_memories`). */
  syncedAt?: number
  metadata: {
    patientId?: string
    topic?: string
    summary?: boolean
  }
}

export type VectorEntryInput = Omit<VectorEntry, 'id' | 'embedding'> & {
  embedding: number[] | Float32Array
}

interface SynapLocalAIDBSchema extends DBSchema {
  vectors: {
    key: string
    value: VectorEntry
    indexes: { timestamp: number; sessionId: string }
  }
}

const DEFAULT_DB_NAME = 'synap-local-ai'
const STORE_NAME = 'vectors'
const DB_VERSION = 1

const MS_PER_DAY = 24 * 60 * 60 * 1000

/** Padrão LGPD / plano Gemma offline — retenção de memórias locais em dias. */
export const DEFAULT_LOCAL_AI_MEMORY_RETENTION_DAYS = 30

export interface LocalVectorStoreOpenOptions {
  /** `0` ou omitido com `undefined` tratado em chamada: sem poda por idade (só `> 0` poda). */
  retentionDays?: number
}

export class LocalVectorStore {
  private readonly dbName: string
  private readonly maxEntries: number
  private readonly pruneTarget: number
  private db: IDBPDatabase<SynapLocalAIDBSchema> | null = null
  private opening: Promise<void> | null = null
  /** Incrementado em `close()` para invalidar `open()` em voo (evita reabrir durante `deleteDB`). */
  private openGeneration = 0

  /**
   * @param dbName — padrão `synap-local-ai`; use outro nome em testes para isolar o IDB.
   * @param maxEntries — janela deslizante (plano: 2000).
   * @param pruneTarget — após poda, mantém até este total (plano: 1800).
   */
  constructor(
    dbName: string = DEFAULT_DB_NAME,
    maxEntries = 2000,
    pruneTarget = 1800,
  ) {
    this.dbName = dbName
    this.maxEntries = maxEntries
    this.pruneTarget = pruneTarget
  }

  /** Fecha a conexão com o IndexedDB (ex.: antes de `deleteDB` em testes ou no logout). */
  close(): void {
    this.openGeneration += 1
    this.opening = null
    if (this.db) {
      this.db.close()
      this.db = null
    }
  }

  /**
   * Remove entradas com `timestamp` anterior a `now - retentionDays`.
   * @returns número de linhas apagadas.
   */
  async purgeExpiredEntries(retentionDays: number): Promise<number> {
    if (retentionDays <= 0) return 0
    const database = await this.ensureOpen()
    const cutoff = Date.now() - retentionDays * MS_PER_DAY
    const all = await database.getAll(STORE_NAME)
    const stale = all.filter((row) => row.timestamp < cutoff)
    if (stale.length === 0) return 0
    await Promise.all(stale.map((row) => database.delete(STORE_NAME, row.id)))
    return stale.length
  }

  async open(options?: LocalVectorStoreOpenOptions): Promise<void> {
    if (this.db) return
    if (this.opening) {
      await this.opening
      return
    }

    const retentionDays = options?.retentionDays
    const generation = this.openGeneration

    const openingPromise = (async () => {
      try {
        const database = await openDB<SynapLocalAIDBSchema>(this.dbName, DB_VERSION, {
          upgrade(db) {
            if (!db.objectStoreNames.contains(STORE_NAME)) {
              const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' })
              store.createIndex('timestamp', 'timestamp')
              store.createIndex('sessionId', 'sessionId')
            }
          },
        })
        if (generation !== this.openGeneration) {
          database.close()
          return
        }
        this.db = database
        if (retentionDays !== undefined && retentionDays > 0) {
          if (generation !== this.openGeneration) return
          await this.purgeExpiredEntries(retentionDays)
        }
      } catch (cause) {
        this.db = null
        throw new AppError('Nao foi possivel abrir o armazenamento vetorial local.', {
          code: 'NETWORK',
          cause,
        })
      }
    })()

    this.opening = openingPromise
    try {
      await openingPromise
    } finally {
      if (this.opening === openingPromise) {
        this.opening = null
      }
    }
  }

  private async ensureOpen(): Promise<IDBPDatabase<SynapLocalAIDBSchema>> {
    await this.open()
    if (!this.db) {
      throw new AppError('Base vetorial local nao inicializada.', { code: 'NOT_CONFIGURED' })
    }
    return this.db
  }

  private toNumberArray(embedding: number[] | Float32Array): number[] {
    return embedding instanceof Float32Array ? Array.from(embedding) : [...embedding]
  }

  async add(entry: VectorEntryInput): Promise<string> {
    const database = await this.ensureOpen()
    const id = crypto.randomUUID()
    const row: VectorEntry = {
      id,
      content: entry.content,
      embedding: this.toNumberArray(entry.embedding),
      role: entry.role,
      timestamp: entry.timestamp,
      sessionId: entry.sessionId,
      metadata: { ...entry.metadata },
    }

    try {
      await database.add(STORE_NAME, row)
    } catch (cause) {
      throw new AppError('Falha ao gravar entrada vetorial local.', {
        code: 'UNKNOWN',
        cause,
      })
    }

    await this.pruneIfNeeded()
    return id
  }

  async pruneIfNeeded(): Promise<void> {
    const database = await this.ensureOpen()
    const total = await database.count(STORE_NAME)
    if (total <= this.maxEntries) return

    const toRemove = total - this.pruneTarget
    if (toRemove <= 0) return

    const all = await database.getAll(STORE_NAME)
    all.sort((a, b) => a.timestamp - b.timestamp)
    const victims = all.slice(0, toRemove)

    const tx = database.transaction(STORE_NAME, 'readwrite')
    await Promise.all(victims.map((row) => tx.store.delete(row.id)))
    await tx.done
  }

  async searchSimilar(
    queryEmbedding: Float32Array,
    options?: { topK?: number; sessionId?: string; minScore?: number },
  ): Promise<Array<VectorEntry & { score: number }>> {
    const database = await this.ensureOpen()
    const topK = options?.topK ?? 5
    const minScore = options?.minScore ?? 0.3
    const sessionId = options?.sessionId

    let rows = await database.getAll(STORE_NAME)
    if (sessionId !== undefined) {
      rows = rows.filter((r) => r.sessionId === sessionId)
    }

    const scored: Array<VectorEntry & { score: number }> = []
    for (const row of rows) {
      const emb = new Float32Array(row.embedding)
      const score = embeddingEngine.cosineSimilarity(queryEmbedding, emb)
      if (score < minScore) continue
      scored.push({ ...row, score })
    }

    scored.sort((a, b) => b.score - a.score)
    return scored.slice(0, topK)
  }

  async clear(sessionId?: string): Promise<void> {
    const database = await this.ensureOpen()

    if (sessionId === undefined) {
      await database.clear(STORE_NAME)
      return
    }

    const rows = await database.getAllFromIndex(
      STORE_NAME,
      'sessionId',
      IDBKeyRange.only(sessionId),
    )
    await Promise.all(rows.map((row) => database.delete(STORE_NAME, row.id)))
  }

  async count(): Promise<number> {
    const database = await this.ensureOpen()
    return database.count(STORE_NAME)
  }

  async getRecent(limit: number, sessionId?: string): Promise<VectorEntry[]> {
    const database = await this.ensureOpen()
    let rows = await database.getAll(STORE_NAME)
    if (sessionId !== undefined) {
      rows = rows.filter((r) => r.sessionId === sessionId)
    }
    rows.sort((a, b) => b.timestamp - a.timestamp)
    return rows.slice(0, limit)
  }

  async getById(id: string): Promise<VectorEntry | undefined> {
    const database = await this.ensureOpen()
    return database.get(STORE_NAME, id)
  }

  async markSynced(ids: string[], syncedAtMs: number): Promise<void> {
    if (ids.length === 0) return
    const database = await this.ensureOpen()
    const tx = database.transaction(STORE_NAME, 'readwrite')
    for (const id of ids) {
      const row = await tx.store.get(id)
      if (row) {
        await tx.store.put({ ...row, syncedAt: syncedAtMs })
      }
    }
    await tx.done
  }

  async deleteByIds(ids: string[]): Promise<void> {
    if (ids.length === 0) return
    const database = await this.ensureOpen()
    await Promise.all(ids.map((id) => database.delete(STORE_NAME, id)))
  }
}

export const localVectorStore = new LocalVectorStore()
